import {useState} from 'react'
import {AlertIcon, LockIcon, XIcon} from '@primer/octicons-react'
import {Button, IconButton} from '@primer/react'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {verifiedFetch} from '@github-ui/verified-fetch'
import type {ConnectionStatus} from '../../types/session'
import styles from './CLIChatInputHeader.module.css'

const CLI_REMOTE_SESSION_NOTICE_KEY = 'copilot_cli_remote_session_info'

interface CLIChatInputHeaderProps {
  agentTaskId?: string
  connectionStatus?: ConnectionStatus
  /** Whether the CLI remote session notice has been dismissed (from route query) */
  cliRemoteNoticeDismissed?: boolean
  /** Whether the task is shared — suppresses the "only you can view remote sessions" banner */
  isShared?: boolean
}

function RemoteSessionInfoBanner({onDismiss}: {onDismiss: () => void}) {
  return (
    <div className={styles.infoContainer}>
      <LockIcon size={16} className={styles.infoIcon} />
      <span className={styles.infoText}>Only you can view remote sessions.</span>
      <Button
        as="a"
        href="https://docs.github.com/copilot/concepts/agents/copilot-cli/about-remote-access"
        rel="noreferrer noopener"
        target="_blank"
        size="small"
        variant="default"
        className={styles.docsButton}
        aria-label="Learn more about remote session privacy"
      >
        Learn more
      </Button>
      <IconButton
        variant="invisible"
        icon={XIcon}
        aria-label="Dismiss remote session banner"
        size="small"
        onClick={onDismiss}
      />
    </div>
  )
}

function OfflineSessionBanner({resumeCommand}: {resumeCommand: string}) {
  return (
    <div className={styles.container}>
      <AlertIcon size={16} className={styles.icon} />
      <span className={styles.text}>
        Session is offline. Connect it again with <code className={styles.command}>{resumeCommand}</code>
      </span>
      <CopyToClipboardButton
        textToCopy={resumeCommand}
        variant="invisible"
        ariaLabel="Copy resume command"
        className={styles.copyButton}
      />
    </div>
  )
}

export function CLIChatInputHeader({
  agentTaskId,
  connectionStatus,
  cliRemoteNoticeDismissed,
  isShared,
}: CLIChatInputHeaderProps) {
  const [localDismissed, setLocalDismissed] = useState(false)
  const showOffline =
    isFeatureEnabled('copilot_mission_control_cli_session_status') && connectionStatus === 'disconnected'
  const showInfoBanner = !isShared && cliRemoteNoticeDismissed === false && !localDismissed

  const handleDismissNotice = async () => {
    setLocalDismissed(true)
    await verifiedFetch(`/settings/dismiss-notice/${CLI_REMOTE_SESSION_NOTICE_KEY}`, {method: 'POST'})
  }

  if (!showInfoBanner && !showOffline) return null

  const resumeCommand = agentTaskId ? `copilot --remote --resume ${agentTaskId}` : 'copilot --remote --resume'

  return (
    <div className={styles.root}>
      {showInfoBanner ? (
        <RemoteSessionInfoBanner onDismiss={handleDismissNotice} />
      ) : (
        <OfflineSessionBanner resumeCommand={resumeCommand} />
      )}
    </div>
  )
}
